import React, { useState, useEffect, useCallback } from 'react';
import { Shield, FileCheck, Fingerprint } from 'lucide-react';
import '../styles/erp-theme.css';

import { OverviewTab }      from './LicensorConsoleOverview';
import { GridTab }          from './LicensorConsoleGridTab';
import { ClientDetail }     from './LicensorConsoleClientDetail';
import { PricingTabGrid }   from './LicensorConsolePricingTab';
import { RelationsTab }     from './LicensorConsoleRelations';

const TABS = [
  { id: 'overview', label: 'Overview',         icon: Shield     },
  { id: 'registry', label: 'Registry Archive', icon: FileCheck  },
  { id: 'users',    label: 'System Users',     icon: Fingerprint},
];

/**
 * LicensorConsole — Super-admin tenant management surface.
 *
 * `forceTab`      when set, hides the tab strip and locks the view to that
 *                 inner tab. Acceptable values:
 *                   overview | clients | licenses | registry | payments |
 *                   metadata | children | users | pricing | relations |
 *                   tier-config
 * `pricingSubTab` forces the pricing-tab sub-selector (uams | privileges |
 *                 integrity | industry | pricing).
 */
export function LicensorConsole({ forceTab = null, pricingSubTab = null } = {}) {
  const lockedTab = forceTab === 'tier-config' ? 'pricing' : forceTab;
  const [tab, setTab] = useState(lockedTab || 'overview');
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    if (lockedTab) { setTab(lockedTab); setSelectedClient(null); }
  }, [lockedTab]);

  useEffect(() => {
    const consumeHint = () => {
      const ts  = parseInt(sessionStorage.getItem('erp-nav-subtab-ts') || '0', 10);
      const sub = sessionStorage.getItem('erp-nav-subtab');
      if (!sub || Date.now() - ts > 3000) return;
      if (['pricing','uams','privileges','integrity','industry'].includes(sub)) { setTab('pricing'); setSelectedClient(null); }
    };
    consumeHint();
    const handler = (e) => {
      const subTab = e.detail?.subTab;
      if (['pricing','uams','privileges','integrity','industry'].includes(subTab)) { setTab('pricing'); setSelectedClient(null); }
    };
    window.addEventListener('erp-navigate', handler);
    return () => window.removeEventListener('erp-navigate', handler);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1400 }}>
      {!lockedTab && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, borderBottom: '2px solid #1e293b', paddingBottom: 12 }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => { setTab(t.id); setSelectedClient(null); }} data-testid={`licensor-tab-${t.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: active ? '#3b82f6' : 'transparent', color: active ? '#f8fafc' : '#94a3b8', border: active ? 'none' : '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em', transition: 'background 150ms, color 150ms' }}>
                <Icon size={12} /> {t.label}
              </button>
            );
          })}
        </div>
      )}

      {tab === 'overview'  && <OverviewTab />}
      {tab === 'clients'   && (
        <>
          <GridTab
            endpoint="/licensor/clients" testIdBase="licensor-clients" title="360° Customer Records"
            idField="ClientId" nameField="ClientName" mutationTable="ClientMaster" pk="ClientId"
            copyKeys={[{ field: 'ClientId', label: 'Copy ClientId' }, { field: 'license_id', label: 'Copy license_id' }]}
            onSelect={(c) => { setSelectedClient(c.ClientId); setTab('clientDetail'); }}
            columns={[
              { field: 'ClientId',            header: 'ClientId',     visible: true,  frozen: true },
              { field: 'ClientName',          header: 'Name',         visible: true  },
              { field: 'CompName',            header: 'Company',      visible: true  },
              { field: 'EmailId',             header: 'Email',        visible: true  },
              { field: 'PhoneNo1',            header: 'Phone',        visible: true  },
              { field: 'Status',              header: 'Status',       visible: true  },
              { field: 'tier_code',           header: 'Tier',         visible: true  },
              { field: 'paymentStatus',       header: 'Pay Status',   visible: true  },
              { field: 'paymentAmount',       header: 'Pay Amt',      visible: false },
              { field: 'license_id',          header: 'license_id',   visible: true  },
              { field: 'LatestLicenseTier',   header: 'Lic Tier',     visible: true  },
              { field: 'LatestLicenseStatus', header: 'Lic Status',   visible: true  },
              { field: 'LicenseCount',        header: 'L#',           visible: true  },
              { field: 'ChildUserCount',      header: 'CLID#',        visible: true  },
              { field: 'RegistryCount',       header: 'Reg#',         visible: false },
              { field: 'MetadataCount',       header: 'Meta#',        visible: false },
              { field: 'MClientId',           header: 'MClientId',    visible: false },
              { field: 'ClientGuid',          header: 'ClientGuid',   visible: false },
              { field: 'City',                header: 'City',         visible: false },
              { field: 'Industry',            header: 'Industry',     visible: false },
              { field: 'Packages',            header: 'Packages',     visible: false },
              { field: 'SQLDBName',           header: 'DB Name',      visible: true  },
              { field: 'SQLSchemaName',       header: 'Schema',       visible: true  },
              { field: 'DataBaseCreated',     header: 'LiveDB',       visible: true  },
            ]}
          />
        </>
      )}
      {tab === 'clientDetail' && <ClientDetail clientId={selectedClient} onBack={() => setTab('clients')} />}
      {tab === 'licenses'  && <GridTab
        endpoint="/licensor/licenses" testIdBase="licensor-licenses" title="All Licenses (ClientMaster_License)"
        idField="LicenseId" nameField="ClientName" mutationTable="ClientMaster_License" pk="LicenseId"
        copyKeys={[{ field: 'LicenseKey', label: 'Copy LicenseKey' }, { field: 'ProductId', label: 'Copy ProductId' }, { field: 'ClientId', label: 'Copy ClientId' }, { field: 'LicenseId', label: 'Copy LicenseId' }]}
        voidAction={{ endpoint: (row) => `/licensor/license/${row.LicenseId}/void`, label: 'Void license' }}
        columns={[
          { field: 'ClientId',        header: 'ClientId',    visible: true,  frozen: true },
          { field: 'ClientName',      header: 'Client',      visible: true  },
          { field: 'LicenseKey',      header: 'License Key', visible: true  },
          { field: 'LicenseTier',     header: 'Tier',        visible: true  },
          { field: 'Amount',          header: 'Amount',      visible: true  },
          { field: 'NumberOfUsers',   header: 'Users',       visible: true  },
          { field: 'NumberOfSystems', header: 'Systems',     visible: true  },
          { field: 'HasSupport',      header: 'Support',     visible: false },
          { field: 'IssuedDate',      header: 'Issued',      visible: true  },
          { field: 'ExpiryDate',      header: 'Expiry',      visible: true  },
          { field: 'DaysLeft',        header: 'Days Left',   visible: true  },
          { field: 'Status',          header: 'Status',      visible: true  },
          { field: 'ProductId',       header: 'ProductId',   visible: false },
        ]}
      />}
      {tab === 'registry'  && <GridTab
        endpoint="/licensor/registry" testIdBase="licensor-registry" title="License Registry (LicenseRegistry)"
        idField="LicenseId" nameField="ClientName" mutationTable="LicenseRegistry" pk="LicenseId"
        copyKeys={[{ field: 'LicenseId', label: 'Copy LicenseId' }, { field: 'ClientId', label: 'Copy ClientId' }]}
        voidAction={{ endpoint: (row) => `/licensor/registry/${row.LicenseId}/revoke`, label: 'Revoke registry' }}
        columns={[
          { field: 'ClientId',    header: 'ClientId',  visible: true, frozen: true },
          { field: 'ClientName',  header: 'Client',    visible: true },
          { field: 'LicenseId',   header: 'LicenseId', visible: true },
          { field: 'IssuedAt',    header: 'Issued',    visible: true },
          { field: 'ExpiresAt',   header: 'Expires',   visible: true },
          { field: 'Packages',    header: 'Packages',  visible: true },
          { field: 'MaxUsers',    header: 'MaxUsers',  visible: true },
          { field: 'MaxDevices',  header: 'MaxDev',    visible: true },
          { field: 'IssuedBy',    header: 'Issued By', visible: true },
          { field: 'Status',      header: 'Status',    visible: true },
          { field: 'RevokedDate', header: 'Revoked',   visible: false },
          { field: 'RevokedBy',   header: 'RevokedBy', visible: false },
        ]}
      />}
      {tab === 'payments'  && <GridTab
        endpoint="/licensor/payments" testIdBase="licensor-payments" title="Payments (ClientMaster + latest license)"
        idField="ClientId" nameField="ClientName" mutationTable="ClientMaster" pk="ClientId"
        copyKeys={[{ field: 'ClientId', label: 'Copy ClientId' }, { field: 'Orderid', label: 'Copy Orderid' }]}
        columns={[
          { field: 'ClientId',            header: 'ClientId',     visible: true, frozen: true },
          { field: 'ClientName',          header: 'Client',       visible: true },
          { field: 'CompName',            header: 'Company',      visible: true },
          { field: 'EmailId',             header: 'Email',        visible: true },
          { field: 'PaymentGateway',      header: 'Gateway',      visible: true },
          { field: 'paymentStatus',       header: 'Status',       visible: true },
          { field: 'paymentAmount',       header: 'Pay Amount',   visible: true },
          { field: 'TotalAmt',            header: 'TotalAmt',     visible: true },
          { field: 'SetupCost',           header: 'Setup',        visible: false },
          { field: 'Orderid',             header: 'OrderId',      visible: true },
          { field: 'tier_code',           header: 'Tier',         visible: true },
          { field: 'LatestLicenseAmount', header: 'Lic Amount',   visible: true },
          { field: 'LatestLicenseTier',   header: 'Lic Tier',     visible: true },
          { field: 'LatestLicenseStatus', header: 'Lic Status',   visible: true },
        ]}
      />}
      {tab === 'metadata'  && <GridTab
        endpoint="/licensor/signup-metadata" testIdBase="licensor-metadata" title="Signup Metadata (ClientSignup_Metadata)"
        idField="MetaId" nameField="ClientName" mutationTable={null} pk="MetaId"
        copyKeys={[{ field: 'ClientId', label: 'Copy ClientId' }, { field: 'MetaId', label: 'Copy MetaId' }, { field: 'ULID', label: 'Copy ULID' }]}
        columns={[
          { field: 'ClientId',                   header: 'ClientId',  visible: true,  frozen: true },
          { field: 'ClientName',                 header: 'Client',    visible: true  },
          { field: 'LocationStatus',             header: 'Location',  visible: true  },
          { field: 'Latitude',                   header: 'Lat',       visible: true  },
          { field: 'Longitude',                  header: 'Lng',       visible: true  },
          { field: 'Platform',                   header: 'Platform',  visible: true  },
          { field: 'BiometricLivenessCompleted', header: 'Biometric', visible: true  },
          { field: 'RequestIp',                  header: 'IP',        visible: true  },
          { field: 'Timezone',                   header: 'TZ',        visible: false },
          { field: 'UserAgent',                  header: 'UA',        visible: false },
          { field: 'CreatedDate',                header: 'Created',   visible: true  },
          { field: 'ULID',                       header: 'ULID',      visible: false },
        ]}
      />}
      {tab === 'children'  && <GridTab
        endpoint="/licensor/child-users" testIdBase="licensor-children" title="Sub-Users / CLID (ClientMaster_Child)"
        idField="SlNo" nameField="ClientName" mutationTable="ClientMaster_Child" pk="SlNo"
        copyKeys={[{ field: 'ClientId', label: 'Copy ClientId' }, { field: 'EmailId', label: 'Copy Email' }]}
        columns={[
          { field: 'ClientId',         header: 'ClientId',  visible: true,  frozen: true },
          { field: 'UserType',         header: 'UserType',  visible: true  },
          { field: 'ClientName',       header: 'Name',      visible: true  },
          { field: 'EmailId',          header: 'Email',     visible: true  },
          { field: 'IpAddress',        header: 'IP',        visible: true  },
          { field: 'GlobalAddress',    header: 'Global IP', visible: true  },
          { field: 'Status',           header: 'Status',    visible: true  },
          { field: 'ParentClientName', header: 'Parent',    visible: true  },
          { field: 'MClientId',        header: 'MClientId', visible: false },
          { field: 'CreatedDate',      header: 'Created',   visible: false },
        ]}
      />}
      {tab === 'users'     && <GridTab
        endpoint="/licensor/system-users" testIdBase="licensor-users" title="System Users (WebApp.SystemUsers)"
        idField="UserId" nameField="Username" mutationTable={null} pk="UserId"
        copyKeys={[{ field: 'UserId', label: 'Copy UserId' }, { field: 'Email', label: 'Copy Email' }]}
        columns={[
          { field: 'Username',     header: 'Username',   visible: true, frozen: true },
          { field: 'Email',        header: 'Email',      visible: true  },
          { field: 'FullName',     header: 'Full Name',  visible: true  },
          { field: 'Role',         header: 'Role',       visible: true  },
          { field: 'IsSuperAdmin', header: 'SuperAdmin', visible: true  },
          { field: 'IsActive',     header: 'Active',     visible: true  },
          { field: 'LastLogin',    header: 'Last Login', visible: true  },
          { field: 'Department',   header: 'Department', visible: false },
          { field: 'PhoneNumber',  header: 'Phone',      visible: false },
          { field: 'CreatedAt',    header: 'Created',    visible: false },
        ]}
      />}
      {tab === 'pricing'   && <PricingTabGrid forceTab={forceTab} pricingSubTab={pricingSubTab} />}
      {tab === 'relations' && <RelationsTab />}
    </div>
  );
}

export default LicensorConsole;
